import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { UserDefinedVisitorSegmentData } from './types';
import { visitorSegmentsDb } from './user-defined-segments-db';

const QUERY_KEY = ['userDefinedVisitorSegments'];

/**
 * Hook to help query, create and update Adaptive content user-defined visitor segments stored in IndexedDB.
 */
export function useUserDefinedVisitorSegments() {
    const queryClient = useQueryClient();

    const fetchSegments = async () => {
        const segments = await visitorSegmentsDb
            .table<UserDefinedVisitorSegmentData, number>('segments')
            .toArray();
        return segments;
    };

    const { data: userDefinedSegments } = useQuery<Array<UserDefinedVisitorSegmentData>>({
        queryKey: QUERY_KEY,
        queryFn: fetchSegments,
    });

    const addVisitorSegmentMutation = useMutation({
        mutationFn: async (
            newSegmentData: Omit<UserDefinedVisitorSegmentData, 'id' | 'lastUpdatedAt'>
        ) => {
            const addedSegment = { ...newSegmentData, lastUpdatedAt: Date.now() };
            const id = await visitorSegmentsDb
                .table<Omit<UserDefinedVisitorSegmentData, 'id'>, number>('segments')
                .add(addedSegment);
            return { id, ...addedSegment };
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
    });

    const addVisitorSegmentSync = (
        segment: Parameters<typeof addVisitorSegmentMutation.mutate>[0],
        onSegmentCreated: (addedSegment: UserDefinedVisitorSegmentData) => void
    ) => {
        addVisitorSegmentMutation.mutate(segment, {
            onSuccess: async (addedSegment) => {
                await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
                onSegmentCreated(addedSegment);
            },
        });
    };

    const updateVisitorSegmentMutation = useMutation({
        mutationFn: async (
            updatedSegmentData: Omit<UserDefinedVisitorSegmentData, 'lastUpdatedAt'>
        ) => {
            const updatedSegment = {
                ...updatedSegmentData,
                lastUpdatedAt: Date.now(),
            };
            await visitorSegmentsDb.table('segments').update(updatedSegmentData.id, updatedSegment);
            return updatedSegment;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
    });

    const deleteSegmentMutation = useMutation({
        mutationFn: async (id: number) => {
            await visitorSegmentsDb.table('segments').delete(id);
            return id;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
    });

    return {
        userDefinedSegments,
        addVisitorSegment: addVisitorSegmentMutation.mutateAsync,
        addVisitorSegmentSync,
        updateVisitorSegment: updateVisitorSegmentMutation.mutateAsync,
        deleteVisitorSegment: deleteSegmentMutation.mutateAsync,
    };
}
